import axios, { AxiosResponse, AxiosStatic, AxiosError } from "axios";
import PagedDataResultDTO from "features/shared/dtos/PagedDataResultDTO";
import { injectable } from "inversify";
import ApiRequest from "./ApiRequest";
import DataQuery from "./DataQuery";
import PostRequest from "./PostRequest";
import DeleteRequest from "./DeleteRequest";
import PostFileRequest from "./PostFileRequest";
import PostResponse from './PostResponse';
import IDataHelper from "./IDataHelper";
import { inversifyContainer } from "infrastructure/ioc/ioc.container";
import { IocTypes } from "infrastructure/ioc/ioc.types";
import { getDynamicConfig } from "infrastructure/configuration/configurationHelper";

const dynamicConfig = getDynamicConfig();

@injectable()
export default abstract class RepositoryBase<T> {
  private readonly _dataHelper: IDataHelper;

  axios: AxiosStatic;
  baseUrl: string | undefined;

  constructor() {
    this.axios = axios;
    this.baseUrl = dynamicConfig.flexiApiUrl || "";
    this._dataHelper = inversifyContainer.get<IDataHelper>(IocTypes.DataHelper);
  }

  public async get(pDataQuery: DataQuery) {
    return await new Promise<Array<T>>((resolve, reject) => {
      axios
        .get(this._dataHelper.getUrl(pDataQuery), {
          cancelToken: pDataQuery.cancelToken,
          params: pDataQuery.params,
        })
        .then((response: AxiosResponse<Array<T>>) => {
          resolve(response.data);
        })
        .catch((error) => {
          reject();
        });
    });
  }

  public async getPagedResult(pDataQuery: DataQuery) {
    return await new Promise<PagedDataResultDTO<T>>((resolve, reject) => {
      axios
        .get(this._dataHelper.getUrl(pDataQuery, this.baseUrl), {
          cancelToken: pDataQuery.cancelToken,
          params: pDataQuery.params,
        })
        .then((response: AxiosResponse<PagedDataResultDTO<T>>) => {
          resolve(response.data);
        })
        .catch((error) => {
          reject();
        });
    });
  }

  public async getOne(pDataQuery: DataQuery) {
    return await new Promise<T>((resolve, reject) => {
      axios
        .get(this._dataHelper.getUrl(pDataQuery, this.baseUrl), {
          cancelToken: pDataQuery.cancelToken,
          params: pDataQuery.params,
        })
        .then((response: AxiosResponse<T>) => {
          resolve(response.data);
        })
        .catch((error) => {
          reject();
        });
    });
  }

  public post<TResult>(postRequest: PostRequest<T>): Promise<PostResponse<TResult>> {
    return this.postRequest(postRequest, postRequest.data);   
  }

  public postWithFile<TResult>(postFileRequest: PostFileRequest<T>): Promise<PostResponse<TResult>> {
    const { files, filesKey, multiple, object, objectKey } = postFileRequest;
    let formData = new FormData();

    // append one or multiple files
    if (files) {
      if (files instanceof FormData) {
        for (const [key, file] of files.entries()) {
          formData.append(filesKey, file);
          
          if (!multiple)
            break;
      }
      } else if (files instanceof FileList) {
        for (const file of files) {
          formData.append(filesKey, file);
    
          if (!multiple)
            break;
        }  
      }
    }

    // append json object
    if (objectKey && typeof object === 'object' && object !== null) {
      formData.append(objectKey, JSON.stringify(object));
    }


    return this.postRequest(postFileRequest, formData);
  }

  public async delete<TResult>(deleteRequest: DeleteRequest<T>): Promise<PostResponse<TResult>> {
    const executeDelete = () => {
      return axios.delete(
        this._dataHelper.getUrl(deleteRequest, this.baseUrl),
        {
          cancelToken: deleteRequest.cancelToken,
          params: deleteRequest.params,
          data: deleteRequest.data !== undefined ? deleteRequest.data : undefined
        }
      );
    };

    return this.executeRequest(executeDelete);
  }

  public async put<TResult>(putRequest: PostRequest<T>): Promise<PostResponse<TResult>>  {
    const executePut = () => {
      return axios.put(this._dataHelper.getUrl(putRequest, this.baseUrl), putRequest.data, {
        cancelToken: putRequest.cancelToken,
        params: putRequest.params
      });
    };

    return this.executeRequest(executePut);
  }

  private async postRequest<TResult>(apiReuest: ApiRequest, data: any): Promise<PostResponse<TResult>> {
    const executePost = () => {
      return axios.post(this._dataHelper.getUrl(apiReuest, this.baseUrl), data, {
        cancelToken: apiReuest.cancelToken,
        params: apiReuest.params
      });
    }
    
    return this.executeRequest(executePost);
  }

  private async executeRequest<TResult>(execute: () => Promise<AxiosResponse<any>>): Promise<PostResponse<TResult>> {
    try {
      let response = await execute();

      return { status: response.status, data: response.data };
    } catch (error) {
      let err: AxiosError = (error as AxiosError);

      if (err.response) {
        return { status: err.response.status, data: err.response.data };
      } else {
        return { status: 500 };
      }
    }
  }
}
